import { Component, inject, OnInit, OnDestroy, Input } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  FormControl,
  FormGroup,
  FormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DialogService } from 'primeng/dynamicdialog';
import {
  CursoService,
  ProgramaService,
  AreaProgramaFormacaoService,
  AreaFormacaoService,
  InstituicaoEnsinoService,
} from '../../../api/services';
import { Curso, Programa } from '../../../api/models';
import { Subscription, forkJoin } from 'rxjs';
import { ApiService } from '@core/providers/http';
import { CommonModule } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import { InstituicoesEnsinoDetalheCursoComponent } from '../instituicoes-ensino-detalhe-curso/instituicoes-ensino-detalhe-curso.component';
import { CategoriaInstituicaoEnsinoService } from '../../../api/services/categoria-instituicao-ensino.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-instituicoes-ensino-cadastrar-cursos',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    Select,
    InputTextModule,
    ButtonModule,
    CheckboxModule,
    InputNumberModule,
    ToastModule,
  ],
  providers: [
    CursoService,
    ProgramaService,
    AreaFormacaoService,
    ApiService,
    AreaProgramaFormacaoService,
    CategoriaInstituicaoEnsinoService,
    InstituicaoEnsinoService,
    MessageService,
  ],
  templateUrl: './instituicoes-ensino-cadastrar-cursos.component.html',
  styleUrls: ['./instituicoes-ensino-cadastrar-cursos.component.scss'],
  standalone: true,
})
export class InstituicoesEnsinoCadastrarCursosComponent
  implements OnInit, OnDestroy
{
  areaFormacaoList: any[] = [];
  categoriaInstituicaoEnsinoList: any[] = [];
  nivelAcademicoOptions: any[] = [];
  private fb = inject(FormBuilder);
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  programaService = inject(ProgramaService);
  messageService = inject(MessageService);
  areaProgramaFormacaoService = inject(AreaProgramaFormacaoService);
  areaFormacaoService = inject(AreaFormacaoService);
  dialogService = inject(DialogService);
  @Input() curso: Curso;
  cursos: Curso[] = [];
  programas: any[] = [];
  areaFormacao: any[] = [];
  private subscriptions = new Subscription();
  institutionForm: FormGroup;
  private instituicaoEnsinoService = inject(InstituicaoEnsinoService);
  unidadeOrganicaOptions: any[] = [];
  unidade: any;
  private isInternalChange = false;

  constructor() {
    this.curso = this.config?.data.curso;

    this.institutionForm = this.fb.group({
      id: [0],
      designacao: [''],
      sigla: [''],
      codigo: [''],
      areaFormacaoID: [null],
      nivelAcademico: [null],
      categoriaInstituicaoEnsinoID: [null],
      valorMensalidade: [0],
      instituicaoEnsinoID: [this.insituicaoEnsinoID],
      cursoID: [null],
      programaID: [''],
      IsNewCurse: [false],
    });

    this.institutionForm.controls['IsNewCurse'].valueChanges.subscribe(
      (isNew) => {
        if (this.isInternalChange) {
          this.isInternalChange = false;
          return;
        }

        if (isNew) {
          // ✅ Modo novo curso: campos de criação obrigatórios, cursoID livre
          [
            'designacao',
            'areaFormacaoID',
            'nivelAcademico',
            'categoriaInstituicaoEnsinoID',
          ].forEach((field) => {
            this.institutionForm
              .get(field)
              ?.setValidators([Validators.required]);
            this.institutionForm.get(field)?.setValue(null);
            this.institutionForm.get(field)?.updateValueAndValidity();
          });
          this.institutionForm.get('cursoID')?.clearValidators();
          this.institutionForm.get('cursoID')?.setValue(null);
          this.institutionForm.get('cursoID')?.updateValueAndValidity();
        } else {
          // ✅ Modo curso existente: cursoID obrigatório, campos de criação livres
          this.institutionForm
            .get('cursoID')
            ?.setValidators([Validators.required]);
          this.institutionForm.get('cursoID')?.setValue(null);
          this.institutionForm.get('cursoID')?.updateValueAndValidity();

          [
            'designacao',
            'areaFormacaoID',
            'nivelAcademico',
            'categoriaInstituicaoEnsinoID',
          ].forEach((field) => {
            this.institutionForm.get(field)?.clearValidators();
            this.institutionForm.get(field)?.setValue(null);
            this.institutionForm.get(field)?.updateValueAndValidity();
          });
        }
      },
    );

    const data = this.config?.data;
    if (data) {
      console.log('Dados recebidos no construtor:', data);
      this.areaFormacaoList = data.areaFormacaoList || [];
      this.categoriaInstituicaoEnsinoList =
        data.categoriaInstituicaoEnsinoList || [];
      this.nivelAcademicoOptions = data.nivelAcademicoOptions || [];
      this.cursos = data.cursos || [];
      this.unidade = data.unidade || null;
      this.institutionForm.patchValue({
        instituicaoEnsinoID: data.curso?.unidadeOrganicaID || this.unidade?.id,
      });
    }
  }

  ngOnInit() {
    if (this.config?.data) {
      console.log('Dados recebidos no ngOnInit:', this.config.data);
      this.areaFormacaoList = this.config.data.areaFormacaoList || [];
      this.categoriaInstituicaoEnsinoList =
        this.config.data.categoriaInstituicaoEnsinoList || [];
      this.nivelAcademicoOptions = this.config.data.nivelAcademicoOptions || [];
      this.unidade = this.config.data.unidade || null;
    }

    // ✅ Carrega lista primeiro, só depois faz patch para garantir seleção correta
    this.loadAllUnidadeOrganica(() => {
      if (this.config?.data?.curso?.id) {
        this.institutionForm.addControl(
          'id',
          new FormControl(this.config.data.curso.id),
        );
        this.patchValues(this.config.data.curso);
      }
      this.institutionForm.patchValue({
        instituicaoEnsinoID: this.unidade.id,
      });
      this.institutionForm.get('instituicaoEnsinoID')?.disable();
    });
  }

  get insituicaoEnsinoID() {
    return this.config?.data.instituicaoEnsinoID;
  }

  loadAllUnidadeOrganica(onComplete?: () => void) {
    this.instituicaoEnsinoService
      .list({
        InstituicaoPaiID: this.insituicaoEnsinoID,
        PageSize: 1000,
      })
      .subscribe((res) => {
        this.unidadeOrganicaOptions = res.data;

        // if (!this.unidadeOrganicaOptions.length) {
        this.institutionForm.get('instituicaoEnsinoID')?.disable();
        console.log(
          'Nenhuma unidade orgânica encontrada para a instituição de ensino ID:',
          this.insituicaoEnsinoID,
          this.curso?.unidadeOrganicaID,
        );
        this.institutionForm.patchValue({
          instituicaoEnsinoID:
            this.config?.data?.curso?.unidadeOrganicaID ||
            this.unidade?.id ||
            this.insituicaoEnsinoID,
        });
        onComplete?.();
      });
  }

  patchValues(curso: any): void {
    const controls = this.institutionForm.controls;
    delete curso['IsNewCurse'];

    Object.keys(curso).forEach((key) => {
      if (controls[key]) {
        controls[key].setValue(curso[key]);
      }
    });

    // ✅ Garante que a unidade orgânica é aplicada com a lista já carregada
    const unidadeId = curso.instituicaoEnsinoID || curso.unidadeOrganicaID;
    if (unidadeId) {
      this.institutionForm.patchValue({ instituicaoEnsinoID: unidadeId });
    }

    this.disableIsNewCurse();
  }

  disableIsNewCurse() {
    this.isInternalChange = true;
    this.institutionForm.controls['IsNewCurse'].disable();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onAreaChange(event: any) {
    this.areaProgramaFormacaoService
      .list({ areaFormacaoID: event.value, PageSize: 300 })
      .subscribe((res) => {
        this.programas = res.data.map((programa: any) => ({
          designacao: programa?.programaFormacao?.nome,
          id: programa?.programaFormacao?.id,
        }));
      });
  }

  save(): void {
    if (this.institutionForm.valid) {
      const payload = this.institutionForm.getRawValue();
      this.ref.close({
        ...payload,
        instituicaoEnsinoID: payload.instituicaoEnsinoID,
      });
    } else {
      this.institutionForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Formulário inválido',
        detail: 'Por favor, preencha todos os campos obrigatórios.',
      });
    }
  }

  cancel() {
    this.ref.close();
  }

  showDetails() {
    this.dialogService.open(InstituicoesEnsinoDetalheCursoComponent, {
      showHeader: false,
      width: '50%',
      modal: true,
    });
  }
}
